import { z } from 'zod'

import type { CaseStudyLong } from './longCaseStudy'

export type { CaseStudyLong } from './longCaseStudy'
export { isLongCaseStudy } from './longCaseStudy'

const seoFieldsSchema = z
  .object({
    title: z.string().min(5).max(70).optional(),
    description: z.string().min(10).max(170).optional(),
    image: z.string().url().optional(),
  })
  .optional()

const sharedFrontmatterSchema = z.object({
  title: z.string().min(5),
  slug: z.string().min(2),
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD'),
  tags: z.array(z.string()).default([]),
  seo: seoFieldsSchema,
})

export const noteFrontmatterSchema = sharedFrontmatterSchema.extend({
  excerpt: z.string().min(20),
  preview: z.string().min(2).optional(),
  sourceUrl: z.string().url().optional(),
  readTime: z.number().int().positive().optional(),
  audience: z.string().min(1).optional(),
  contributors: z.number().int().nonnegative().optional(),
  quote: z
    .object({
      body: z.string().min(10),
      attribution: z.string().optional(),
      name: z.string().optional(),
      role: z.string().optional(),
      company: z.string().optional(),
      photo_url: z.string().optional(),
      photo_alt: z.string().optional(),
    })
    .optional(),
  workflow_schema: z
    .object({
      heading: z.string().optional(),
      type: z.enum(['mermaid', 'text']).optional(),
      body: z.string().min(10),
      position: z
        .object({
          after_heading: z.string().min(1).optional(),
          after_paragraph: z.string().min(1).optional(),
        })
        .optional(),
    })
    .optional(),
  faq: z
    .object({
      heading: z.string().optional(),
      items: z
        .array(
          z.object({
            question: z.string().min(5),
            answer: z.string().min(5),
          }),
        )
        .min(1),
    })
    .optional(),
  related_case_study: z
    .object({
      slug: z.string().min(2),
      heading: z.string().optional(),
      position: z
        .object({
          after_heading: z.string().min(1).optional(),
          after_paragraph: z.string().min(1).optional(),
        })
        .optional(),
    })
    .optional(),
})

export type SeoFields = z.infer<typeof seoFieldsSchema>

export type Note = z.infer<typeof noteFrontmatterSchema> & {
  type: 'note'
  content: string
}

export type CaseStudy = CaseStudyLong

export type Post = Note | CaseStudy
